import { Button, Modal } from '@clearcover/atlas';
import { PolicyContext } from 'Policies/utils';
import { FormInput } from 'Shared/components';
import { useLazyApi } from 'Shared/services/Api';
import { useNewApiV2 } from 'Shared/services/Api/makeApiHttp';
import { formatDateTimeMiddleUsingTimezone } from 'Shared/utils';
import { Formik } from 'formik';
import React, { FC, useContext } from 'react';
import * as Yup from 'yup';
import doorStyles from '../../PolicyCancellation/styles.module.css';

import styles from './styles.module.css';

const NonRenewalFormSchema = Yup.object().shape({
  notes: Yup.string(),
  reason: Yup.string().required('Please select a reason.'),
});

export const PolicyNonRenewal: FC<{
  policyId: string;
  onSetToNoneRenewal: () => void;
  endDate: string;
}> = ({ policyId, onSetToNoneRenewal, endDate }) => {
  const {
    details: { filingIdentifier, timeZone },
  } = useContext(PolicyContext);
  const [{ data }] = useNewApiV2<{
    reasons: Record<
      'customer_requested' | 'clearcover_initiated' | 'nonrenewal',
      Record<string, string>
    >;
  }>({
    url: `/cancellation/options?productFilingIdentifier=${filingIdentifier}`,
  });

  const [, applyNonRenewal] = useLazyApi({
    url: `policies/${policyId}/setToNonRenew`,
    method: 'POST',
  });
  return (
    <Formik
      initialValues={{
        notes: '',
        reason: '',
      }}
      validationSchema={NonRenewalFormSchema}
      onSubmit={(values, { setStatus }) => {
        setStatus(null);
        return applyNonRenewal({
          data: {
            reason: {
              code: values.reason,
              description: data.reasons['nonrenewal'][values.reason],
            },
            notes: values.notes,
          },
        })
          .then(() => {})
          .catch(({ message }) => {
            setStatus(message);
          });
      }}
      validateOnChange={true}
    >
      {({
        setFieldValue,
        values,
        errors,
        isSubmitting,
        isValid,
        setSubmitting,
        submitForm,
      }) => (
        <div className={doorStyles.door}>
          <div className={doorStyles.header}>
            <h2>Non-Renew</h2>
          </div>
          <div className={doorStyles.body}>
            {data && (
              <FormInput
                variant="selectAutocomplete"
                name="reason"
                onChange={value => {
                  setFieldValue('reason', value);
                }}
                value={values.reason}
                label="Reason for Nonrenewal"
                options={data.reasons['nonrenewal']}
                errorMessage={errors.reason}
              />
            )}
            <FormInput
              variant="textarea"
              name="notes"
              onChange={({ target }) => {
                setFieldValue('notes', target.value);
              }}
              value={values.notes}
              label="Notes"
              errorMessage={errors.notes}
            />
            <h3 className={styles.expirationHeader}>Policy Expiration Date</h3>
            <p>
              Policy will be in effect until{' '}
              {formatDateTimeMiddleUsingTimezone(endDate, timeZone)} at 12:00PM
              CST. Tapping below will ensure the policy will not renew.
            </p>
            <Modal
              toggle={openModal => (
                <Button
                  block
                  data-testid="open-non-renew-policy-modal"
                  variant="danger"
                  isSubmitting={isSubmitting}
                  disabled={!isValid || values.reason === ''}
                  onClick={() => {
                    submitForm()
                      .then(openModal)
                      .finally(() => {
                        setSubmitting(false);
                      });
                  }}
                >
                  Set Policy to Non-Renew
                </Button>
              )}
            >
              {close => (
                <div className={styles.continueModal}>
                  <h3>Policy set not to renew</h3>
                  <p>
                    Policy will be in effect until{' '}
                    {formatDateTimeMiddleUsingTimezone(endDate, timeZone)} at
                    12:00PM CST.
                  </p>
                  <Button
                    block
                    data-testid={'non-renew-confirmation-button'}
                    variant="primary"
                    onClick={() => {
                      close();
                      onSetToNoneRenewal();
                    }}
                  >
                    Continue
                  </Button>
                </div>
              )}
            </Modal>
          </div>
        </div>
      )}
    </Formik>
  );
};
